import { useCallback, useEffect, useMemo, useState } from 'react';

import { Alert, Avatar, Badge, Button, Card, Inline, Spinner, Stack, Text, identityTone } from '../design/index.js';
import {
  fetchNodes,
  fetchShares,
  fetchSentInvites,
  revokeInvite,
  revokeShare,
  updateShare,
} from '../api.js';
import type { InviteSummary, NodeSummary, ShareRecipient } from '../api.js';
import { ShareDialog } from './ShareDialog.js';
import styles from './ShareSettings.module.css';

export interface ShareSettingsProps {
  className?: string;
}

type ListState<T> =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'loaded'; data: T };

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

/** "in 4 days" / "in a few hours" — matches the relative-time voice
 *  `WithingsCard` uses for the past, mirrored onto the future for an
 *  invite's expiry. */
function formatExpiresAt(iso: string): string {
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return 'soon';

  const diffSeconds = Math.round((at - Date.now()) / 1000);
  if (diffSeconds <= 0) return 'about to expire';

  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['day', 60 * 60 * 24],
    ['hour', 60 * 60],
    ['minute', 60],
  ];
  for (const [unit, secondsInUnit] of units) {
    if (diffSeconds >= secondsInUnit || unit === 'minute') {
      return rtf.format(Math.round(diffSeconds / secondsInUnit), unit);
    }
  }
  return 'soon';
}

function initialsFor(displayName: string): string {
  return displayName.trim().slice(0, 1).toUpperCase() || '?';
}

type RowPhase =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'revoke-confirm' }
  | { kind: 'revoking' };

/**
 * One person the owner shares with: their avatar and address, a checkbox per
 * node the owner owns, and the two ways to change what they see. Saving
 * always sends the whole ticked set — `updateShare` REPLACES rather than
 * merges, so there is never a chance of this tab re-adding something another
 * tab just removed.
 */
function RecipientCard({
  recipient,
  ownNodes,
  onUpdated,
  onRevoked,
}: {
  recipient: ShareRecipient;
  ownNodes: NodeSummary[];
  onUpdated: (recipient: ShareRecipient) => void;
  onRevoked: (userId: string) => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(() => new Set(recipient.nodes.map((n) => n.id)));
  const [phase, setPhase] = useState<RowPhase>({ kind: 'idle' });
  const [error, setError] = useState<string | null>(null);

  // A share changed under us (another tab, or this one's own save) — reset
  // the checkboxes to match rather than fighting the server's version.
  useEffect(() => {
    setSelected(new Set(recipient.nodes.map((n) => n.id)));
  }, [recipient.nodes]);

  const originalIds = useMemo(() => new Set(recipient.nodes.map((n) => n.id)), [recipient.nodes]);
  const dirty =
    selected.size !== originalIds.size || [...selected].some((id) => !originalIds.has(id));
  const revoking = phase.kind === 'revoking';
  const showingRevokeConfirm = phase.kind === 'revoke-confirm' || revoking;
  const busy = phase.kind === 'saving' || revoking;

  function toggle(nodeId: string) {
    if (busy) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  }

  async function handleSave() {
    setPhase({ kind: 'saving' });
    setError(null);
    try {
      const result = await updateShare(recipient.userId, [...selected]);
      // Unticking every node and saving is the quiet way to stop sharing —
      // the same outcome the explicit "Stop sharing" button below produces,
      // just without the confirmation, since nothing was removed a person
      // did not already ask to remove one box at a time.
      if (result.nodes.length === 0) {
        onRevoked(recipient.userId);
        return;
      }
      setPhase({ kind: 'idle' });
      onUpdated(result);
    } catch (err) {
      setPhase({ kind: 'idle' });
      setError(errorMessage(err, 'Could not update what this person can see.'));
    }
  }

  async function handleRevoke() {
    setPhase({ kind: 'revoking' });
    setError(null);
    try {
      await revokeShare(recipient.userId);
      onRevoked(recipient.userId);
    } catch (err) {
      setPhase({ kind: 'revoke-confirm' });
      setError(errorMessage(err, 'Could not remove this person’s access.'));
    }
  }

  return (
    <Card data-testid="share-recipient-card">
      <Stack gap={4}>
        <Inline gap={3} align="center">
          <Avatar
            size="md"
            tone={identityTone(recipient.userId)}
            src={recipient.avatarUrl ?? undefined}
            initials={initialsFor(recipient.displayName)}
            alt=""
          />
          <Stack gap={1}>
            <Text size="sm" weight="medium">{recipient.displayName}</Text>
            <Text size="xs" color="muted">{recipient.email}</Text>
          </Stack>
        </Inline>

        {ownNodes.length === 0 ? (
          <Text size="sm" color="muted">
            You have nothing to share yet — add something to track first.
          </Text>
        ) : (
          <Stack gap={2} className={styles.nodeList}>
            {ownNodes.map((node) => {
              const inputId = `share-${recipient.userId}-${node.id}`;
              return (
                <label key={node.id} htmlFor={inputId} className={styles.nodeCheck}>
                  <input
                    id={inputId}
                    type="checkbox"
                    checked={selected.has(node.id)}
                    onChange={() => toggle(node.id)}
                    disabled={busy}
                  />
                  <Text size="sm">{node.title}</Text>
                </label>
              );
            })}
          </Stack>
        )}

        {error && (
          <Alert variant="error" title="Something went wrong" data-testid="share-recipient-error">
            {error}
          </Alert>
        )}

        <Inline gap={3} align="center" wrap>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleSave}
            disabled={!dirty || busy}
            data-testid="share-recipient-save"
          >
            {phase.kind === 'saving' ? 'Saving…' : 'Save changes'}
          </Button>
          {phase.kind === 'saving' && <Spinner size="sm" aria-label="Saving what this person can see" />}

          {!showingRevokeConfirm ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPhase({ kind: 'revoke-confirm' })}
              disabled={busy}
              data-testid="share-recipient-revoke-button"
            >
              Stop sharing
            </Button>
          ) : (
            <Inline gap={3} align="center" wrap data-testid="share-recipient-revoke-confirm">
              <Text size="sm" color="secondary">
                Stop sharing with {recipient.displayName}? They will no longer see any of your nodes.
              </Text>
              <Button variant="danger" size="sm" onClick={handleRevoke} disabled={revoking}>
                {revoking ? 'Stopping…' : 'Yes, stop sharing'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={revoking}
                onClick={() => setPhase({ kind: 'idle' })}
              >
                Cancel
              </Button>
            </Inline>
          )}
        </Inline>
      </Stack>
    </Card>
  );
}

/**
 * One still-pending invite the owner sent: who it was addressed to, what it
 * offered, when it expires, and a way to take it back. Accepted and declined
 * invites never reach this list — accepted ones already show up as a
 * `RecipientCard` above, and a declined one has nothing left to act on.
 */
function SentInviteRow({
  invite,
  onRevoked,
}: {
  invite: InviteSummary;
  onRevoked: (id: string) => void;
}) {
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleRevoke() {
    setRevoking(true);
    setError(null);
    try {
      await revokeInvite(invite.id);
      onRevoked(invite.id);
    } catch (err) {
      setRevoking(false);
      setError(errorMessage(err, 'Could not take back this invite.'));
    }
  }

  return (
    <Card data-testid="sent-invite-row">
      <Stack gap={3}>
        <Inline gap={3} justify="space-between" wrap align="center">
          <Stack gap={1}>
            <Text size="sm" weight="medium">{invite.invitedEmail}</Text>
            <Text size="xs" color="muted">
              {invite.nodes.map((n) => n.title).join(', ') || 'Nothing selected'} · expires{' '}
              {formatExpiresAt(invite.expiresAt)}
            </Text>
          </Stack>
          <Badge variant="neutral">Pending</Badge>
        </Inline>

        {error && (
          <Alert variant="error" title="Couldn't take back this invite" data-testid="sent-invite-error">
            {error}
          </Alert>
        )}

        <Inline gap={3} align="center">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRevoke}
            disabled={revoking}
            data-testid="sent-invite-revoke"
          >
            {revoking ? 'Taking back…' : 'Take back'}
          </Button>
          {revoking && <Spinner size="sm" aria-label="Taking back this invite" />}
        </Inline>
      </Stack>
    </Card>
  );
}

/**
 * The settings section for managing sharing: sending a new share, editing or
 * revoking what each person already sees, and taking back an invite that
 * has not been answered yet.
 *
 * The three lists (the caller's own nodes, who they share with, and their
 * sent invites) load independently, so a failure in one never blocks the
 * others from rendering.
 */
export function ShareSettings({ className }: ShareSettingsProps) {
  const [nodesState, setNodesState] = useState<ListState<NodeSummary[]>>({ kind: 'loading' });
  const [sharesState, setSharesState] = useState<ListState<ShareRecipient[]>>({ kind: 'loading' });
  const [invitesState, setInvitesState] = useState<ListState<InviteSummary[]>>({ kind: 'loading' });
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadNodes = useCallback(async () => {
    try {
      const nodes = await fetchNodes();
      setNodesState({ kind: 'loaded', data: nodes });
    } catch (err) {
      setNodesState({ kind: 'error', message: errorMessage(err, 'Could not load your nodes.') });
    }
  }, []);

  const loadShares = useCallback(async () => {
    try {
      const shares = await fetchShares();
      setSharesState({ kind: 'loaded', data: shares });
    } catch (err) {
      setSharesState({ kind: 'error', message: errorMessage(err, 'Could not load who you share with.') });
    }
  }, []);

  const loadInvites = useCallback(async () => {
    try {
      const invites = await fetchSentInvites();
      setInvitesState({ kind: 'loaded', data: invites });
    } catch (err) {
      setInvitesState({
        kind: 'error',
        message: errorMessage(err, "Could not load the invites you've sent."),
      });
    }
  }, []);

  useEffect(() => {
    void loadNodes();
    void loadShares();
    void loadInvites();
  }, [loadNodes, loadShares, loadInvites]);

  function handleRecipientUpdated(recipient: ShareRecipient) {
    setSharesState((prev) =>
      prev.kind === 'loaded'
        ? { kind: 'loaded', data: prev.data.map((r) => (r.userId === recipient.userId ? recipient : r)) }
        : prev,
    );
  }

  function handleRecipientRevoked(userId: string) {
    setSharesState((prev) =>
      prev.kind === 'loaded' ? { kind: 'loaded', data: prev.data.filter((r) => r.userId !== userId) } : prev,
    );
  }

  function handleInviteRevoked(id: string) {
    setInvitesState((prev) =>
      prev.kind === 'loaded' ? { kind: 'loaded', data: prev.data.filter((i) => i.id !== id) } : prev,
    );
  }

  function handleInviteSent(invite: InviteSummary) {
    setInvitesState((prev) =>
      prev.kind === 'loaded' ? { kind: 'loaded', data: [invite, ...prev.data] } : prev,
    );
    setDialogOpen(false);
  }

  const ownNodes = nodesState.kind === 'loaded' ? nodesState.data : [];
  const dialogNodes = useMemo(() => ownNodes.map((n) => ({ id: n.id, title: n.title })), [ownNodes]);
  const pendingInvites =
    invitesState.kind === 'loaded' ? invitesState.data.filter((i) => i.status === 'pending') : [];

  return (
    <Stack gap={6} className={className} data-testid="share-settings">
      <Stack gap={4}>
        <Inline justify="space-between" align="center" wrap>
          <Text size="xs" color="muted" className={styles.label}>People you share with</Text>
          <Button size="sm" onClick={() => setDialogOpen(true)} data-testid="share-settings-share-button">
            Share
          </Button>
        </Inline>

        {sharesState.kind === 'loading' && (
          <Inline gap={3} align="center">
            <Spinner size="sm" aria-label="Loading who you share with" />
            <Text size="sm" color="muted">Loading…</Text>
          </Inline>
        )}

        {sharesState.kind === 'error' && (
          <Alert variant="error" title="Couldn't load who you share with" data-testid="shares-error">
            {sharesState.message}
          </Alert>
        )}

        {sharesState.kind === 'loaded' && sharesState.data.length === 0 && (
          <Card data-testid="shares-empty">
            <Text size="sm" color="secondary">
              You aren't sharing any nodes yet. Use "Share" to invite someone by email — they'll see it in
              their notifications if they already use log.gy, or you can send them the link yourself.
            </Text>
          </Card>
        )}

        {sharesState.kind === 'loaded' && sharesState.data.length > 0 && (
          <Stack gap={3}>
            {sharesState.data.map((recipient) => (
              <RecipientCard
                key={recipient.userId}
                recipient={recipient}
                ownNodes={ownNodes}
                onUpdated={handleRecipientUpdated}
                onRevoked={handleRecipientRevoked}
              />
            ))}
          </Stack>
        )}
      </Stack>

      <Stack gap={4}>
        <Text size="xs" color="muted" className={styles.label}>Invitations still outstanding</Text>

        {invitesState.kind === 'loading' && (
          <Inline gap={3} align="center">
            <Spinner size="sm" aria-label="Loading your sent invites" />
            <Text size="sm" color="muted">Loading…</Text>
          </Inline>
        )}

        {invitesState.kind === 'error' && (
          <Alert variant="error" title="Couldn't load your sent invites" data-testid="invites-error">
            {invitesState.message}
          </Alert>
        )}

        {invitesState.kind === 'loaded' && pendingInvites.length === 0 && (
          <Card data-testid="invites-empty">
            <Text size="sm" color="secondary">
              Nothing waiting on a reply. An invite you send sits here until the other person accepts,
              declines, or it expires.
            </Text>
          </Card>
        )}

        {invitesState.kind === 'loaded' && pendingInvites.length > 0 && (
          <Stack gap={3}>
            {pendingInvites.map((invite) => (
              <SentInviteRow key={invite.id} invite={invite} onRevoked={handleInviteRevoked} />
            ))}
          </Stack>
        )}
      </Stack>

      {dialogOpen && (
        <ShareDialog nodes={dialogNodes} onClose={() => setDialogOpen(false)} onSent={handleInviteSent} />
      )}
    </Stack>
  );
}
